/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SecondJoin } from '../GoToMeetingPages/SecondJoin/SecondJoin';
import { FirstJoin } from '../GoToMeetingPages/FirstJoin/FirstJoin';
import { SecondJoinManual } from '../GoToMeetingPages/SecondJoinManual/SecondJoinManual';
import {
  initiateStatusPolling,
  Status as LaunchStatus,
} from '../../../services/LaunchStatusService';
import { Ongoing } from '../../StatusPages/Ongoing/Ongoing';
import { Error } from '../../StatusPages/Error/Error';
import { createLogger } from '../../../services/LoggerService';
import { isGrayOutOnJoinPageDesired } from '../../../services/ProductCompatibilityService';
import { SessionContext } from '../../../context/SessionContext';
import { isInstantJoinOfferedAndSupported } from '../../../services/MeetingService';
import {
  hadNativeJoinSuccessInPast,
  setManualSecondJoinOfferedInThisSession,
  shouldUseNative,
  wasManualSecondJoinOfferedInThisSession,
  wasNativeApplicationUsedInTheLast30Days,
} from '../../../lib/nativeUsageStore';
import { GrayOverlay } from '../../GrayOverlay/GrayOverlay';
import { Timer } from '../../../utils/timer';
import {
  addSuccessFragment,
  hasSuccessFragment,
} from '../../../services/UrlService';
import { SecondJoinWithIframe } from '../GoToMeetingPages/SecondJoin/SecondJoinWithIframe';
import { JoinSuccessComponent } from '../../StatusPages/Success/JoinSuccessComponent';
import { GoToJoinOngoing } from '../GoToPages/StatusPages/GoToJoinOngoing';
import { GoToError } from '../../StatusPages/Error/GoToError';
import { GoToJoinSuccess } from '../GoToPages/StatusPages/GoToJoinSuccess';
import { ThemeType } from '../../../services/IndexPageReaderService';
import { GoToFirstJoin } from '../GoToPages/JoinFlowPages/FirstJoin/GoToFirstJoin';
import { GoToSecondJoin } from '../GoToPages/JoinFlowPages/SecondJoin/GoToSecondJoin';
import { GoToSecondJoinAutomatic } from '../GoToPages/JoinFlowPages/SecondJoin/GoToSecondJoinAutomatic';

const log = createLogger('JoinPage');

export enum JoinFlowState {
  tryingSecondJoin,
  offeringFirstJoin,
  offeringSecondJoin,
  joinAppStarted,
  joinSuccessful,
  initializing,
  errorEncountered,
  tryingSecondJoinWithIframe,
}

interface MyWindow extends Window {
  timerForSecondJoin?: Timer;
}

export const JoinFlow: React.FC = () => {
  const [joinFlowState, setJoinFlowState] = useState<JoinFlowState>(
    JoinFlowState.initializing
  );
  const {
    meetingId,
    meetingInfo,
    launcherServiceLaunchToken,
    openerLaunchToken,
    theme,
  } = useContext(SessionContext);
  const joinFlowStateRef = useRef(joinFlowState);
  joinFlowStateRef.current = joinFlowState;
  const launcherServiceLaunchTokenRef = useRef(launcherServiceLaunchToken);
  launcherServiceLaunchTokenRef.current = launcherServiceLaunchToken;

  const updateLaunchState = (newState: LaunchStatus) => {
    if (newState === LaunchStatus.Success) {
      log.info('Launch Successful');
      setJoinFlowState(JoinFlowState.joinSuccessful);
    } else if (newState === LaunchStatus.LaunchedApp) {
      log.info('App Started');
      setJoinFlowState(JoinFlowState.joinAppStarted);
    } else if (newState === LaunchStatus.Error) {
      log.warn('Error encountered');
      setJoinFlowState(JoinFlowState.errorEncountered);
    }
    log.info('New State:', newState);
  };

  const transitionTo = (state: JoinFlowState) => {
    (window as MyWindow)['timerForSecondJoin'] = new Timer(() => {
      if (joinFlowStateRef.current === state) {
        if (
          wasNativeApplicationUsedInTheLast30Days() &&
          !wasManualSecondJoinOfferedInThisSession()
        ) {
          transitionToOfferingSecondJoin();
        } else {
          setJoinFlowState(JoinFlowState.offeringFirstJoin);
        }
      }
    }, 2000);
  };

  const beginStatusPollingAfterTimeout = (timeout: number) => {
    // When starting the status polling directly, Chrome thinks the page is still loading.
    // ToDo: No longer use JSONP in LaunchStatusService (see GTMTWO-35513, needs CORS headers set in Launcher Service)
    window.setTimeout(
      () =>
        initiateStatusPolling(
          launcherServiceLaunchTokenRef.current,
          updateLaunchState
        ),
      timeout
    );
  };

  const showSecondJoinPageAndTransitionToFirstJoinAfterTimeout =
    // Does not open the opener, only shows the page.
    // The opener will be opened by something else (e.g. a href link)
    useCallback(() => {
      transitionTo(JoinFlowState.tryingSecondJoin);
      setJoinFlowState(JoinFlowState.tryingSecondJoin);
      beginStatusPollingAfterTimeout(200);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const trySecondJoinAndTransitionToFirstJoinOfferAfterTimeout =
    useCallback(() => {
      transitionTo(JoinFlowState.tryingSecondJoinWithIframe);
      setJoinFlowState(JoinFlowState.tryingSecondJoinWithIframe);
      beginStatusPollingAfterTimeout(200);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const transitionToOfferingSecondJoin = () => {
    setManualSecondJoinOfferedInThisSession();
    setJoinFlowState(JoinFlowState.offeringSecondJoin);
  };

  useEffect(() => {
    if (hasSuccessFragment()) {
      setJoinFlowState(JoinFlowState.offeringSecondJoin);
      return;
    } else {
      if (
        isInstantJoinOfferedAndSupported(meetingInfo) &&
        !shouldUseNative() &&
        !wasManualSecondJoinOfferedInThisSession()
      ) {
        transitionToOfferingSecondJoin();
        return;
      } else {
        trySecondJoinAndTransitionToFirstJoinOfferAfterTimeout();
      }
    }
  }, [
    meetingInfo,
    meetingId,
    openerLaunchToken,
    trySecondJoinAndTransitionToFirstJoinOfferAfterTimeout,
  ]);

  const GoToPages: { [key in JoinFlowState]: JSX.Element } = {
    [JoinFlowState.initializing]: <Fragment />,
    [JoinFlowState.joinAppStarted]: (
      <GoToJoinOngoing
        onTryAgain={() => {
          addSuccessFragment();
          setJoinFlowState(JoinFlowState.offeringSecondJoin);
        }}
      />
    ),
    [JoinFlowState.errorEncountered]: <GoToError />,
    [JoinFlowState.joinSuccessful]: (
      <GoToJoinSuccess onTryAgain={transitionToOfferingSecondJoin} />
    ),
    [JoinFlowState.offeringFirstJoin]: <GoToFirstJoin />,
    [JoinFlowState.offeringSecondJoin]: (
      <GoToSecondJoin
        onStartButtonClick={
          showSecondJoinPageAndTransitionToFirstJoinAfterTimeout
        }
      />
    ),
    [JoinFlowState.tryingSecondJoin]: (
      <GoToJoinOngoing
        onTryAgain={() => {
          addSuccessFragment();
          setJoinFlowState(JoinFlowState.offeringSecondJoin);
        }}
      />
    ),
    [JoinFlowState.tryingSecondJoinWithIframe]: (
      <GoToSecondJoinAutomatic
        onTryAgain={() => {
          addSuccessFragment();
          setJoinFlowState(JoinFlowState.offeringSecondJoin);
        }}
        onLaunchedSuccessfully={() =>
          setJoinFlowState(JoinFlowState.joinAppStarted)
        }
        onHandlerNotRegistered={() =>
          setJoinFlowState(JoinFlowState.offeringFirstJoin)
        }
      />
    ),
  };

  const G2MPages: { [key in JoinFlowState]: JSX.Element } = {
    [JoinFlowState.initializing]: <Fragment />,
    [JoinFlowState.joinAppStarted]: (
      <Ongoing
        onTryAgain={() => {
          addSuccessFragment();
          setJoinFlowState(JoinFlowState.offeringSecondJoin);
        }}
      />
    ),
    [JoinFlowState.errorEncountered]: <Error />,
    [JoinFlowState.joinSuccessful]: (
      <JoinSuccessComponent onTryAgain={transitionToOfferingSecondJoin} />
    ),
    [JoinFlowState.offeringFirstJoin]: <FirstJoin />,
    [JoinFlowState.offeringSecondJoin]: (
      <SecondJoinManual
        onStartButtonClick={
          showSecondJoinPageAndTransitionToFirstJoinAfterTimeout
        }
      />
    ),
    [JoinFlowState.tryingSecondJoin]: <SecondJoin />,
    [JoinFlowState.tryingSecondJoinWithIframe]: (
      <SecondJoinWithIframe
        onLaunchedSuccessfully={() =>
          setJoinFlowState(JoinFlowState.joinAppStarted)
        }
        onHandlerNotRegistered={() =>
          setJoinFlowState(JoinFlowState.offeringFirstJoin)
        }
      />
    ),
  };

  function shouldShowGrayOverlay() {
    return (
      !hasSuccessFragment() &&
      isGrayOutOnJoinPageDesired &&
      hadNativeJoinSuccessInPast() &&
      (joinFlowState === JoinFlowState.tryingSecondJoin ||
        joinFlowState === JoinFlowState.tryingSecondJoinWithIframe ||
        joinFlowState === JoinFlowState.offeringFirstJoin ||
        (joinFlowState === JoinFlowState.offeringSecondJoin &&
          shouldUseNative()))
    );
  }

  type ThemeToStateSelection = {
    [key in ThemeType]: { [key: string]: JSX.Element };
  };

  const ThemeToJoinStateSelection: ThemeToStateSelection = {
    [ThemeType.GoToTheme]: GoToPages,
    [ThemeType.GoToMeetingTheme]: G2MPages,
  };
  const JoinStateSelection = ThemeToJoinStateSelection[theme.themeType];

  return (
    (
      <Fragment>
        {shouldShowGrayOverlay() && <GrayOverlay />}
        {JoinStateSelection[joinFlowState]}
      </Fragment>
    ) ?? null
  );
};
